import { useState, useEffect, useRef } from "react";
import { FONT, C, CheckIcon, ChevronDown, PlayIcon } from "../lib/programConstants";
import { fetchInstanceSets, upsertSet, bulkUpsertSets, updateBlockStatus } from "../services/WorkoutEndUserService";

// ─── Save Status Indicator ──────────────────────────────────────────────────

function ThreeDotSpinner() {
  return (
    <span style={{ display: "flex", gap: 2, alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 4, height: 4, borderRadius: 2, background: C.muted,
          animation: `dotBounce 0.6s ${i * 0.15}s infinite alternate`,
        }} />
      ))}
      <style>{`@keyframes dotBounce { from { opacity: 0.3; transform: translateY(0); } to { opacity: 1; transform: translateY(-3px); } }`}</style>
    </span>
  );
}

function SaveIndicator({ status }) {
  if (status === "saving") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: C.muted }}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.3"/>
        <path d="M12 2c5.52 0 10 4.48 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
        </path>
      </svg>
    );
  }
  if (status === "saved") {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ color: C.done }}>
        <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return null;
}

// ─── YouTube Video Modal ────────────────────────────────────────────────────

function VideoModal({ videoUrl, onClose }) {
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]+)/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);
  if (!videoId) return null;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.8)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 480, background: "#000", borderRadius: 12, overflow: "hidden",
      }}>
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&origin=https://www.nasm.org`}
            title="Exercise Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
          <a href={videoUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#aaa", textDecoration: "underline", fontFamily: FONT }}>
            Open in YouTube
          </a>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "#fff", fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: FONT, padding: "4px 8px",
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Rest Timer ─────────────────────────────────────────────────────────────

function RestTimer({ restSeconds }) {
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(restSeconds);
  const intervalRef = useRef(null);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) { clearInterval(intervalRef.current); setRunning(false); setFinished(true); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = () => { clearInterval(intervalRef.current); setRunning(false); setFinished(false); setTimeLeft(restSeconds); };
  const toggle = () => { if (finished) { reset(); return; } setRunning((r) => !r); };
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div style={{
      background: finished ? "#f0faf0" : "#f7f7f7",
      borderRadius: 10, padding: "12px 14px",
      display: "flex", alignItems: "center", gap: 12, marginTop: 12,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: FONT, color: C.text, minWidth: 44 }}>
        {mins}:{String(secs).padStart(2, "0")}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT }}>
          {finished ? "Rest Complete" : running ? "Resting..." : "Rest Timer"}
        </div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: FONT }}>{restSeconds}s programmed</div>
      </div>
      <button onClick={toggle} style={{
        padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
        fontFamily: FONT, fontSize: 13, fontWeight: 700,
        background: finished ? "#e8f5e9" : running ? C.chip : "#2b7a78", color: finished ? C.done : running ? C.text : "#fff",
      }}>{finished ? "Reset" : running ? "Pause" : "Start"}</button>
    </div>
  );
}

// ─── Set Row ────────────────────────────────────────────────────────────────

function SetRow({ setData, columns, onUpdate, disabled }) {
  const [localValues, setLocalValues] = useState(setData);
  const [saveStatus, setSaveStatus] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => { setLocalValues(setData); }, [setData]);

  const showSaved = () => {
    setSaveStatus("saved");
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setSaveStatus(null), 1500);
  };

  const handleBlur = async (field, value) => {
    const numVal = value === "" || value == null ? null : Number(value);
    // Skip save if value hasn't changed
    const currentVal = setData[field] ?? null;
    if (numVal === currentVal) return;
    setSaveStatus("saving");
    try {
      await onUpdate(setData.set_number, { [field]: numVal });
      showSaved();
    } catch { setSaveStatus(null); }
  };

  const handleCheckbox = async () => {
    const newVal = !localValues.is_completed;
    setLocalValues((prev) => ({ ...prev, is_completed: newVal }));
    setSaveStatus("saving");
    try {
      // Per doc: send is_completed + the current values for this set type
      // Only include fields that have actual values (from user edits or loaded data)
      const payload = { is_completed: newVal };
      if (columns.includes("reps") && localValues.reps_completed != null && localValues.reps_completed !== "") {
        payload.reps_completed = Number(localValues.reps_completed);
      }
      if (columns.includes("weight") && localValues.weight_used != null && localValues.weight_used !== "") {
        payload.weight_used = Number(localValues.weight_used);
      }
      if (columns.includes("duration") && localValues.duration_seconds != null && localValues.duration_seconds !== "") {
        payload.duration_seconds = Number(localValues.duration_seconds);
      }
      if (columns.includes("distance") && localValues.distance != null && localValues.distance !== "") {
        payload.distance = Number(localValues.distance);
        payload.distance_unit = "mi";
      }
      await onUpdate(setData.set_number, payload);
      showSaved();
    } catch {
      setLocalValues((prev) => ({ ...prev, is_completed: !newVal }));
      setSaveStatus(null);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      {/* Set number (hidden for cardio) */}
      {columns.includes("weight") || columns.includes("reps") || (columns.includes("duration") && !columns.includes("distance")) ? (
        <div style={{ width: 24, fontSize: 14, fontWeight: 500, color: C.muted, fontFamily: FONT }}>
          {setData.set_number}
        </div>
      ) : null}

      {/* Input fields — order matches column headers */}
      {columns.includes("weight") && (
        <div style={{ flex: 1 }}>
          <input type="number" min="0" disabled={disabled}
            value={localValues.weight_used ?? ""}
            onChange={(e) => setLocalValues((p) => ({ ...p, weight_used: e.target.value }))}
            onBlur={(e) => handleBlur("weight_used", e.target.value)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.borderLight}`,
              fontSize: 15, fontFamily: FONT, fontWeight: 500, color: C.text,
              background: disabled ? C.chip : "#f9f9f9", outline: "none",
            }}
          />
        </div>
      )}

      {columns.includes("reps") && (
        <div style={{ flex: 1 }}>
          <input type="number" min="0" disabled={disabled}
            value={localValues.reps_completed ?? ""}
            onChange={(e) => setLocalValues((p) => ({ ...p, reps_completed: e.target.value }))}
            onBlur={(e) => handleBlur("reps_completed", e.target.value)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.borderLight}`,
              fontSize: 15, fontFamily: FONT, fontWeight: 500, color: C.text,
              background: disabled ? C.chip : "#f9f9f9", outline: "none",
            }}
          />
        </div>
      )}

      {columns.includes("duration") && (
        <div style={{ flex: 1 }}>
          <input type="number" min="0" disabled={disabled}
            value={localValues.duration_seconds ?? ""}
            onChange={(e) => setLocalValues((p) => ({ ...p, duration_seconds: e.target.value }))}
            onBlur={(e) => handleBlur("duration_seconds", e.target.value)}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.borderLight}`,
              fontSize: 15, fontFamily: FONT, fontWeight: 500, color: C.text,
              background: disabled ? C.chip : "#f9f9f9", outline: "none",
            }}
          />
        </div>
      )}

      {columns.includes("distance") && (
        <div style={{ flex: 1 }}>
          <input type="number" min="0" step="0.1" disabled={disabled}
            value={localValues.distance ?? ""}
            onChange={(e) => setLocalValues((p) => ({ ...p, distance: e.target.value }))}
            onBlur={(e) => handleBlur("distance", e.target.value)}
            placeholder="mi"
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.borderLight}`,
              fontSize: 15, fontFamily: FONT, fontWeight: 500, color: C.text,
              background: disabled ? C.chip : "#f9f9f9", outline: "none",
            }}
          />
        </div>
      )}

      {/* Checkbox + save indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button onClick={handleCheckbox} disabled={disabled} style={{
          width: 24, height: 24, borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", padding: 0,
          border: localValues.is_completed ? "none" : `2px solid ${C.border}`,
          background: localValues.is_completed ? C.done : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: localValues.is_completed ? "#fff" : "transparent", transition: "all 0.15s ease",
        }}>
          {localValues.is_completed && <CheckIcon />}
        </button>
        <SaveIndicator status={saveStatus} />
      </div>
    </div>
  );
}

// ─── Main Exercise Card ─────────────────────────────────────────────────────

export default function SetLoggingExerciseCard({ exercise, onBlockCompleted, disabled, groupLabel, accentColor, activeRound, totalRounds }) {
  const [expanded, setExpanded] = useState(false);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const hasFetched = useRef(false);

  // The instance_id is the key for API calls (per enduserui.md spec)
  const instanceId = exercise.instanceId || exercise.blockId;
  const isAmrap = exercise.isAmrap || false;
  // Round-based: activeRound is provided by parent (SupersetGroupCard)
  const isRoundBased = (activeRound != null && totalRounds != null && totalRounds > 0) || isAmrap;
  const numRounds = totalRounds || exercise.roundNo || 1;

  const metrics = exercise.metrics || [];
  const getMetricVal = (type) => {
    const m = metrics.find((mt) => mt.metric_type === type);
    return m?.metric_value_num || 0;
  };

  // For round-based (grouped children): rows = sets per round (usually 1 unless child has explicit "sets" metric)
  // For AMRAP: same — sets per round, rounds managed by parent
  // For standalone exercises: numSets from the "sets" metric
  const setsPerRound = isRoundBased ? (getMetricVal("sets") || 1) : 1;
  const numSets = isRoundBased ? setsPerRound : (getMetricVal("sets") || 1);
  const prescribedReps = getMetricVal("reps") || 0;
  const prescribedWeight = getMetricVal("weight") || getMetricVal("load") || 0;
  const restSeconds = getMetricVal("rest") || 0;
  const weightUnit = metrics.find((m) => m.metric_type === "weight" || m.metric_type === "load")?.unit || "lbs";

  // Check for timed exercise — metric_type "time" or "duration" with a numeric value
  const timeMetric = metrics.find((m) => (m.metric_type === "time" || m.metric_type === "duration") && m.metric_value_num > 0);
  const isTimedExercise = !!timeMetric;
  const prescribedDuration = timeMetric ? timeMetric.metric_value_num : 0;

  // Check for distance metric
  const distanceMetric = metrics.find((m) => m.metric_type === "distance" && m.metric_value_num > 0);

  // Check if cardio
  const isCardio = exercise.workoutType === "cardio" || exercise.workout_type === "cardio";

  // Build the notes/prescribed line
  const notesLine = exercise.notes || null;

  // Determine visible columns based on exercise type
  const columns = [];
  if (isCardio) {
    columns.push("duration", "distance");
  } else {
    if (isTimedExercise) columns.push("duration");
    if (distanceMetric) columns.push("distance");
    if (prescribedWeight > 0) columns.push("weight");
    if (prescribedReps > 0) columns.push("reps");
    // Fallback: if no columns detected, show reps
    if (columns.length === 0) columns.push("reps");
  }

  const isCompleted = exercise.status === "completed";

  // Derive completion state from set log data (Option A + B from spec)
  const completedSetsCount = sets.filter((s) => s.is_completed).length;
  const skippedSetsCount = sets.filter((s) => s.is_skipped).length;
  const allSetsCompleted = sets.length > 0 && sets.every((s) => s.is_completed);
  const hasAnySkipped = sets.some((s) => s.is_skipped);
  const hasStarted = hasFetched.current && sets.length > 0 && (completedSetsCount > 0 || skippedSetsCount > 0);

  // The effective "done" state: either from the exercise status OR from all sets being completed
  const isDone = isCompleted || allSetsCompleted;

  const buildDefaultSets = () => {
    if (isRoundBased) {
      // Build sets for all rounds — each round has setsPerRound entries
      const allSets = [];
      for (let r = 1; r <= numRounds; r++) {
        for (let s = 1; s <= setsPerRound; s++) {
          allSets.push({
            set_number: s,
            round_number: r,
            reps_completed: prescribedReps || null,
            weight_used: prescribedWeight || null,
            duration_seconds: prescribedDuration || null,
            is_completed: false,
          });
        }
      }
      return allSets;
    }
    // Standalone: varying set_number, round_number always 1
    return Array.from({ length: numSets }, (_, i) => ({
      set_number: i + 1,
      round_number: 1,
      reps_completed: prescribedReps || null,
      weight_used: prescribedWeight || null,
      duration_seconds: prescribedDuration || null,
      is_completed: false,
    }));
  };

  useEffect(() => {
    // Fetch set data on mount to determine checkmark state (Option B — resume)
    if (!hasFetched.current && instanceId) {
      loadSets();
    }
  }, []);

  useEffect(() => {
    if (expanded && !hasFetched.current && instanceId) {
      loadSets();
    }
  }, [expanded]);

  // When parent adds a new round (AMRAP), add default sets for it
  useEffect(() => {
    if (isRoundBased && totalRounds && hasFetched.current) {
      const maxRoundInSets = sets.length > 0 ? Math.max(...sets.map((s) => s.round_number)) : 0;
      if (totalRounds > maxRoundInSets) {
        const newSets = [];
        for (let r = maxRoundInSets + 1; r <= totalRounds; r++) {
          for (let s = 1; s <= setsPerRound; s++) {
            newSets.push({
              set_number: s,
              round_number: r,
              reps_completed: prescribedReps || null,
              weight_used: prescribedWeight || null,
              duration_seconds: prescribedDuration || null,
              is_completed: false,
            });
          }
        }
        if (newSets.length > 0) {
          setSets((prev) => [...prev, ...newSets]);
        }
      }
    }
  }, [totalRounds]);

  const loadSets = async () => {
    setLoading(true);
    try {
      const response = await fetchInstanceSets(instanceId);
      const data = response.data || [];
      if (data.length === 0) {
        setSets(buildDefaultSets());
        onBlockCompleted?.(exercise.blockId || instanceId, "active", 0);
      } else {
        // Merge loaded data with default structure
        const defaults = buildDefaultSets();
        const loadedSets = defaults.map((def) => {
          const match = data.find((d) => d.set_number === def.set_number && d.round_number === def.round_number);
          return match ? { ...def, ...match } : def;
        });
        // Also add any extra rounds from server (e.g., AMRAP user added more)
        const extraSets = data.filter((d) => !loadedSets.some((ls) => ls.set_number === d.set_number && ls.round_number === d.round_number));
        const merged = [...loadedSets, ...extraSets];
        setSets(merged);

        // Report completed count to parent for progress tracking
        const completedCount = merged.filter((s) => s.is_completed).length;
        const blockId = exercise.blockId || instanceId;
        onBlockCompleted?.(blockId, merged.every((s) => s.is_completed) ? "completed" : "active", completedCount);
      }
      hasFetched.current = true;
    } catch {
      setSets(buildDefaultSets());
      hasFetched.current = true;
    } finally {
      setLoading(false);
    }
  };

  const handleSetUpdate = async (setNumber, data) => {
    // For round-based: setNumber is the actual set_number within the active round
    // round_number comes from the active round (passed from parent)
    const roundNumber = isRoundBased ? (activeRound || 1) : 1;

    const payload = {
      ...data,
      round_number: roundNumber,
      ...(data.weight_used != null && { weight_unit: weightUnit }),
    };

    await upsertSet(instanceId, setNumber, payload);

    setSets((prev) => {
      const updated = prev.map((s) => {
        if (s.set_number === setNumber && s.round_number === roundNumber) {
          return { ...s, ...data };
        }
        return s;
      });

      // Report completed count to parent for progress tracking
      const completedCount = updated.filter((s) => s.is_completed).length;
      const blockId = exercise.blockId || instanceId;
      const allDone = updated.every((s) => s.is_completed);
      setTimeout(() => {
        onBlockCompleted?.(blockId, allDone ? "completed" : "active", completedCount);
      }, 0);

      return updated;
    });
  };

  // Build prescribed payload from metrics (per spec decision tree)
  const buildPrescribedPayload = () => {
    const payload = {};
    for (const m of metrics) {
      switch (m.metric_type) {
        case "reps":
          payload.reps_completed = m.metric_value_num;
          break;
        case "load":
        case "weight":
          if (m.metric_value_num) {
            payload.weight_used = m.metric_value_num;
            payload.weight_unit = m.unit === "kg" ? "kg" : "lbs";
          }
          // text like "bodyweight" → skip
          break;
        case "time":
        case "duration":
          payload.duration_seconds = m.metric_value_num;
          break;
        case "distance":
          payload.distance = m.metric_value_num;
          payload.distance_unit = m.unit || "m";
          break;
        case "rpe":
          payload.rpe_actual = m.metric_value_num;
          break;
        case "calories":
          payload.calories_burned = m.metric_value_num;
          break;
        default:
          break;
      }
    }
    return payload;
  };

  const handleToggleComplete = async () => {
    if (disabled || markingComplete) return;
    setMarkingComplete(true);

    if (isDone) {
      // Undo: mark active
      try {
        await updateBlockStatus(instanceId, "active");
        onBlockCompleted?.(exercise.blockId || instanceId, "active", 0);
        setSets((prev) => prev.map((s) => ({ ...s, is_completed: false })));
      } catch { /* ignore */ }
    } else {
      // Mark complete: bulk-log prescribed values for all sets × rounds
      try {
        const prescribed = buildPrescribedPayload();
        const rounds = isRoundBased ? (totalRounds || numRounds) : 1;
        const setCount = isRoundBased ? setsPerRound : numSets;
        const bulkSets = [];
        for (let round = 1; round <= rounds; round++) {
          for (let set = 1; set <= setCount; set++) {
            bulkSets.push({
              set_number: set,
              round_number: round,
              ...prescribed,
              is_completed: true,
            });
          }
        }
        await bulkUpsertSets(instanceId, bulkSets);
        setSets(bulkSets);
        onBlockCompleted?.(exercise.blockId || instanceId, "completed", bulkSets.length);
      } catch { /* ignore */ }
    }
    setMarkingComplete(false);
  };

  return (
    <>
      {videoOpen && exercise.videoUrl && (
        <VideoModal videoUrl={exercise.videoUrl} onClose={() => setVideoOpen(false)} />
      )}

      <div style={{
        background: C.bg, borderRadius: groupLabel ? 0 : 12, margin: groupLabel ? "0" : "8px 12px",
        border: groupLabel ? "none" : `1px solid ${C.borderLight}`,
        borderBottom: groupLabel ? `1px solid ${C.borderLight}` : undefined,
        boxShadow: groupLabel ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
        opacity: disabled ? 0.6 : 1,
      }}>
        {/* Collapsed header */}
        <div onClick={() => setExpanded((e) => !e)} style={{
          display: "flex", alignItems: "center", padding: "16px",
          cursor: "pointer", gap: 12,
        }}>
          {/* Group label badge (when inside a group) */}
          {groupLabel && (
            <div style={{
              width: 30, height: 30, borderRadius: 15, flexShrink: 0,
              background: accentColor ? `${accentColor}18` : C.chip,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, color: accentColor || C.sub, fontFamily: FONT,
            }}>
              {groupLabel}
            </div>
          )}

          {/* Checkbox */}
          <button onClick={(e) => { e.stopPropagation(); handleToggleComplete(); }} disabled={disabled || markingComplete} style={{
            width: 24, height: 24, borderRadius: 6, cursor: (disabled || markingComplete) ? "not-allowed" : "pointer", padding: 0,
            border: markingComplete ? "none" : isDone ? "none" : hasStarted ? `2px solid ${C.done}` : `2px solid ${C.border}`,
            background: markingComplete ? C.chip : isDone ? C.done : "transparent", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: isDone ? "#fff" : "transparent", transition: "all 0.15s ease",
          }}>
            {markingComplete ? <ThreeDotSpinner /> : isDone ? <CheckIcon /> : null}
          </button>

          {/* Name + metric chips */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: FONT, fontSize: 15, fontWeight: 500, color: isDone ? C.muted : C.text,
              textDecoration: isDone ? "line-through" : "none",
              lineHeight: 1.3, marginBottom: 6,
            }}>
              {exercise.name}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {/* Partial progress indicator */}
              {hasStarted && !isDone && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: "#e8f5e9", fontSize: 11, fontWeight: 600, color: C.done, fontFamily: FONT }}>
                  {completedSetsCount}/{sets.length} done
                </span>
              )}
              {numSets > 0 && !isCardio && !isRoundBased && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: C.chip, fontSize: 11, fontWeight: 600, color: C.sub, fontFamily: FONT }}>
                  {numSets} sets
                </span>
              )}
              {prescribedReps > 0 && !isTimedExercise && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: C.chip, fontSize: 11, fontWeight: 600, color: C.sub, fontFamily: FONT }}>
                  {prescribedReps} reps
                </span>
              )}
              {prescribedWeight > 0 && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: C.chip, fontSize: 11, fontWeight: 600, color: C.sub, fontFamily: FONT }}>
                  {prescribedWeight} {weightUnit}
                </span>
              )}
              {prescribedDuration > 0 && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: C.chip, fontSize: 11, fontWeight: 600, color: C.sub, fontFamily: FONT }}>
                  {prescribedDuration}s
                </span>
              )}
              {isCardio && (
                <span style={{ padding: "3px 8px", borderRadius: 5, background: C.chip, fontSize: 11, fontWeight: 600, color: C.sub, fontFamily: FONT }}>
                  cardio
                </span>
              )}
            </div>
          </div>

          {/* Video button */}
          {exercise.videoUrl && (
            <button onClick={(e) => { e.stopPropagation(); setVideoOpen(true); }} style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
              background: C.chip, cursor: "pointer", color: C.sub, flexShrink: 0,
            }}>
              <PlayIcon />
            </button>
          )}

          {/* Chevron */}
          <div style={{ color: C.muted, flexShrink: 0 }}><ChevronDown open={expanded} /></div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div style={{ padding: "0 16px 16px", animation: "fadeIn 0.15s ease" }}>
            {/* Prescribed text note */}
            {notesLine && (
              <div style={{ fontSize: 12, color: C.muted, fontFamily: FONT, marginBottom: 14, fontStyle: "italic" }}>
                {notesLine}
              </div>
            )}

            {/* Column headers */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {!isCardio && <div style={{ width: 24, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT }}>SET</div>}
              {columns.includes("weight") && <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT }}>WEIGHT</div>}
              {columns.includes("reps") && <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT }}>REPS</div>}
              {columns.includes("duration") && <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT }}>DURATION</div>}
              {columns.includes("distance") && <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT }}>DISTANCE</div>}
              <div style={{ width: 42, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", fontFamily: FONT, textAlign: "center" }}>DONE</div>
            </div>

            {/* Set rows — for round-based, show only the active round's sets */}
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 16, gap: 6 }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{
                    width: 6, height: 6, borderRadius: 3, background: C.muted,
                    animation: `dotBounce 0.6s ${i * 0.15}s infinite alternate`,
                  }} />
                ))}
              </div>
            ) : (
              (isRoundBased
                ? sets.filter((s) => s.round_number === (activeRound || 1))
                : sets
              ).map((s) => (
                <SetRow
                  key={`${s.round_number}-${s.set_number}`}
                  setData={{ ...s, set_number: s.set_number }}
                  columns={columns}
                  onUpdate={handleSetUpdate}
                  disabled={disabled}
                />
              ))
            )}

            {/* Rest timer */}
            {restSeconds > 0 && <RestTimer restSeconds={restSeconds} />}
          </div>
        )}
      </div>
    </>
  );
}
